import { createAppAsyncThunk } from "appThunk";
import { ExtendedResultSet, cubeLoadExtended } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

import { Store } from "modules/customer/insights/portfolioNew/store";
import { DateTime } from "luxon";
import { getDateDimensionNames } from "./customDate";

export class MonthlySales {
    public readonly storeID: string;
    public readonly clientProductCategory: string;
    public readonly yearsPrior: number;
    public readonly monthStartDate: Date;
    public readonly totalSales: number;
    public readonly totalCostOfGoods: number;

    constructor(
        storeID: string,
        clientProductCategory: string,
        yearsPrior: number,
        monthStartDate: Date,
        totalSales: number,
        totalCostOfGoods: number
    ) {
        this.storeID = storeID;
        this.clientProductCategory = clientProductCategory;
        this.yearsPrior = yearsPrior;
        this.monthStartDate = monthStartDate;
        this.totalSales = totalSales;
        this.totalCostOfGoods = totalCostOfGoods;
    }
}


export const loadMonthlySales = createAppAsyncThunk<
    MonthlySales[],
    {
        selectedStore: Store | undefined,
        comparatorStores: Store[] | undefined,
        referenceDate: DateTime,
        useCustomCalendar: boolean
    }
>(
    "customer/insights/portfolio/loadMonthlySales",
    async ({ selectedStore, comparatorStores, referenceDate, useCustomCalendar }, thunkAPI) => {
        try {
            if (!selectedStore || !comparatorStores) {
                return [];
            }
            const oneYearPrior = useCustomCalendar
                ? referenceDate.minus({ weeks: 52 })
                : referenceDate.minus({ months: 12 });

            const startOfCurrentYear = oneYearPrior.plus({ days: 1 }).startOf('day');
            const endOfCurrentYear = referenceDate.endOf('day');

            const startOfPreviousYear = useCustomCalendar
                ? startOfCurrentYear.minus({ weeks: 52 }).startOf('day')
                : startOfCurrentYear.minus({ months: 12 }).startOf('day');
            const endOfPreviousYear = oneYearPrior.endOf('day');

            const comparatorStoreIDs = comparatorStores.map(store => store.id);
            const selectedAndComparatorStoreIDs = [selectedStore.id].concat(...comparatorStoreIDs);

            const dateDimensions = getDateDimensionNames(useCustomCalendar);
            const query = {
                measures: [
                    "F_Sales.SumLineValue",
                    "F_Sales.SumLineCost"
                ],
                dimensions: [
                    "D_Store.StoreNaturalID",
                    "D_ProductCategory.ProductCategory1",
                    `D_Date.${dateDimensions.monthPeriodStartDate}`,
                ],
                timeDimensions: [{
                    dimension: "D_Date.Date",
                    compareDateRange: [
                        [startOfCurrentYear, endOfCurrentYear],
                        [startOfPreviousYear, endOfPreviousYear]
                    ]
                }],
                filters: [{
                    member: "D_Store.StoreNaturalID",
                    operator: "equals",
                    values: selectedAndComparatorStoreIDs
                }],
                limit: 50000
            };

            const resultSet = await thunkAPI.dispatch(cubeLoadExtended(query)) as unknown as ExtendedResultSet<any>;
            const monthlySales: MonthlySales[] = [];

            for (let i = 0; i < resultSet.loadResponses.length; i++) {
                //@ts-ignore
                monthlySales.push(...resultSet.loadResponses[i].data.map(row =>
                    new MonthlySales(
                        row["D_Store.StoreNaturalID"],
                        row["D_ProductCategory.ProductCategory1"],
                        i,
                        row[`D_Date.${dateDimensions.monthPeriodStartDate}`],
                        Number(row["F_Sales.SumLineValue"]),
                        Number(row["F_Sales.SumLineCost"])
                    )
                ));
            }

            return monthlySales;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading MonthlySales.", error));
            return thunkAPI.rejectWithValue(null);
        }
    }
);
